import React from 'react';

/** Indicador de carregamento. `currentColor`: herda a cor do texto ao redor (botão, texto, card). */
export const Spinner: React.FC<{ className?: string }> = ({ className = 'h-4 w-4' }) => (
  <svg aria-hidden="true" viewBox="0 0 24 24" className={`animate-spin ${className}`} fill="none">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="4" />
    <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
  </svg>
);

/**
 * Estado de carregamento de uma área (lista, painel). `role="status"` anuncia a mudança ao leitor de tela.
 * Mantém o texto — testes e leitores de tela dependem dele.
 */
export const LoadingState: React.FC<{ children?: React.ReactNode; className?: string }> = ({
  children = 'Carregando...',
  className = '',
}) => (
  <div role="status" className={`flex items-center justify-center gap-2 py-10 text-sm text-fg-muted ${className}`}>
    <Spinner />
    <span>{children}</span>
  </div>
);

interface EmptyStateProps {
  title: React.ReactNode;
  description?: React.ReactNode;
  /** Ícone opcional acima do título. */
  icon?: React.ReactNode;
  /** Ação principal (um `<Button>`), quando há o que fazer a partir do vazio. */
  action?: React.ReactNode;
  /** Sem borda nem fundo: para usar dentro de uma seção que já tem a própria caixa. */
  bare?: boolean;
  className?: string;
}

/** Estado vazio de uma área: nenhum registro, sem resultado de filtro, etapa ainda sem dados. */
export const EmptyState: React.FC<EmptyStateProps> = ({ title, description, icon, action, bare = false, className = '' }) => (
  <div
    className={`flex flex-col items-center gap-2 px-6 py-10 text-center ${
      bare ? '' : 'rounded-panel border border-dashed border-line-strong bg-surface'
    } ${className}`}
  >
    {icon && <div className="text-fg-subtle" aria-hidden="true">{icon}</div>}
    <p className="text-sm font-semibold text-fg">{title}</p>
    {description && <p className="max-w-md text-sm text-fg-muted">{description}</p>}
    {action && <div className="mt-2">{action}</div>}
  </div>
);
